// @flow
import React, { useEffect, useRef, useState, useMemo } from 'react';
import loadable from '@loadable/component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import StarRating from '@justpark/ui/src/components/StarRating';
import Typography from '@justpark/ui/src/components/Typography';
import DeactivatedSpaceBanner from '@justpark/ui/src/components/DeactivatedSpaceBanner';
import Button from '@justpark/ui/src/components/Button';
import useMatchMediaMatches from '@justpark/ui/src/hooks/useMatchMediaMatches';
import { useSelector } from 'react-redux';
import MapBanner from '../Map/components/MapBanner';
import ListingDetailsTabs from './ListingDetailsTabs';
import useQueryStringTab from '../../hooks/useQueryStringTab';
import BookingDetails from './BookingDetails';
import type { Listing } from '../../generated/graphql';
import DriveUpBanner from './DriveUpBanner';
import AdminBanner from './AdminBanner';
import { selectIsAdminOrIsSuperuser } from '../../stores/auth';
import useTrackViewItem from '../../hooks/useTrackViewItem';
import { gtagTrack } from '../../helpers/Tracking/Analytics';

import styles from '../../../static/scss/components/listingDetails/listingDetails.module.scss';

const Reviews = loadable(() => import('./Reviews'));
const Information = loadable(() => import('./Information'));
const Availability = loadable(() => import('./Availability'));

type Props = {
  location: Listing,
  fetchMore: (any) => void,
  loading: boolean
};

const cypress = 'listing-details';

const useStickyIntersectionRef = () => {
  const stickyAvailabilityRef = useRef();
  const stickyAvailabilityIntersectionRef = useRef();

  useEffect(() => {
    if (
      !('IntersectionObserver' in window) ||
      !stickyAvailabilityIntersectionRef.current
    ) {
      return () => {};
    }

    const observer = new IntersectionObserver(
      ([e]) =>
        stickyAvailabilityRef.current.classList.toggle(
          styles.mobileCheckAvailabilityButtonContainerIsSticky,
          e.intersectionRatio < 1
        ),
      { threshold: [1] }
    );

    observer.observe(stickyAvailabilityIntersectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return { stickyAvailabilityRef, stickyAvailabilityIntersectionRef };
};

const ListingDetails = ({ location, fetchMore, loading }: Props) => {
  const {
    id,
    title,
    staticMapImages,
    acceptsMonthly,
    acceptsFixedTerm
  } = location;
  useTrackViewItem(id, title);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isDriveUpModalOpen, setIsDriveUpModalOpen] = useState(false);

  const isDesktop = useMatchMediaMatches('(min-width: 860px)') === true;

  const isAdminOrIsSuperuser = useSelector(selectIsAdminOrIsSuperuser);

  const mapBannerSizes = useMemo(
    () =>
      staticMapImages?.reduce((acc, item) => {
        acc[item.size] = item.src;
        return acc;
      }, {}),
    [staticMapImages]
  );
  const mapBannerImages = mapBannerSizes || [];

  useEffect(() => {
    Reviews.preload();
    Information.preload();
    Availability.preload();
  }, []);

  useEffect(() => {
    if (isDesktop && isDriveUpModalOpen) {
      setIsDriveUpModalOpen(false);
    }
  }, [isDesktop, isDriveUpModalOpen, setIsDriveUpModalOpen]);

  const [tab, onTabChange] = useQueryStringTab('tab');

  const {
    stickyAvailabilityRef,
    stickyAvailabilityIntersectionRef
  } = useStickyIntersectionRef();

  const isActive = location.active;
  const isDriveup = location.acceptsPrebook === false;

  const {
    address: { lat, lng }
  } = location;
  const showDriveUpBanner = location.acceptsDriveup && !location.acceptsPrebook;
  const showAvailability = !isDriveup && isActive;

  const [searchParams] = useSearchParams();

  const directonsLat = searchParams.get('lat');
  const directonsLng = searchParams.get('lng');

  const mapDirections = useMemo(() => {
    if (directonsLat && directonsLng) {
      return {
        lat: parseFloat(directonsLat),
        lng: parseFloat(directonsLng)
      };
    }

    return null;
  }, [directonsLat, directonsLng]);

  const latLng = useMemo(
    () => ({
      lat,
      lng
    }),
    [lat, lng]
  );

  return (
    <div data-cy="listing-details" className={styles.listingDetails}>
      <MapBanner
        staticMapImages={mapBannerImages}
        latLng={latLng}
        directionsOrigin={mapDirections}
      />
      {isAdminOrIsSuperuser && (
        <div className={styles.adminBannerWrapper}>
          <AdminBanner listingId={location.id} userId={location.userId} />
        </div>
      )}

      <div className={styles.main}>
        <div className={isActive ? styles.left : styles.leftListingDisabled}>
          <div className={styles.header}>
            <Typography className={styles.heading} bold>
              {location.title}
            </Typography>
            <div data-cy={`${cypress}-rating`} className={styles.rating}>
              <StarRating
                hideCount={false}
                size="small"
                numberOfReviews={location.feedback.count}
                rating={location.feedback.averageRating}
                onClickStar={() => onTabChange(2)}
              />
            </div>
          </div>
          <ListingDetailsTabs
            active={tab}
            onChange={onTabChange}
            showAvailabilityTab={showAvailability}
          />

          <div className={tab === 0 ? styles.activeTab : styles.tab}>
            <Information
              className={styles.listingInformation}
              location={location}
              t={t}
            />
          </div>
          <div
            className={
              tab === 1 && showAvailability ? styles.activeTab : styles.tab
            }
          >
            <Availability />
          </div>

          <div className={tab === 2 ? styles.activeTab : styles.tab}>
            <Reviews
              reviews={location.reviews.nodes}
              fetchMore={fetchMore}
              totalCount={location.reviews.totalCount}
              loading={loading}
            />
          </div>
        </div>
        {isActive && (
          <div className={styles.right}>
            {showDriveUpBanner ? (
              <DriveUpBanner
                locationId={location.id}
                isDriveUpModalOpen={isDriveUpModalOpen}
                onCloseDriveUpModal={() => setIsDriveUpModalOpen(false)}
                seasonTicketLink={location.seasonTicketLink}
              />
            ) : (
              <BookingDetails
                acceptsMonthly={acceptsMonthly}
                acceptsFixedTerm={acceptsFixedTerm}
                t={t}
                isBookingModalOpen={isBookingModalOpen}
                onCloseBookingModal={() => setIsBookingModalOpen(false)}
                listingId={location.id}
                extendable={location.acceptsExtensions}
                onBeforeSubmit={() => {
                  gtagTrack('add_to_cart', {
                    listingId: location.id,
                    listingTitle: location.title
                  });
                }}
              />
            )}
          </div>
        )}
      </div>
      {isActive && (
        <>
          <div
            className={styles.mobileCheckAvailabilityButtonContainer}
            ref={stickyAvailabilityRef}
          >
            <Button
              className={styles.mobileCheckAvailabilityButton}
              onClick={() => {
                if (showDriveUpBanner) {
                  setIsDriveUpModalOpen(true);
                } else {
                  setIsBookingModalOpen(true);
                }
              }}
              cypress="mobile-check-availability"
            >
              {showDriveUpBanner
                ? t('listingDetails:paymentInstruction', 'Payment instructions')
                : t('listingDetails:checkAvailability', 'Check availability')}
            </Button>
          </div>
          <div ref={stickyAvailabilityIntersectionRef} />
        </>
      )}
      {!isActive && (
        <DeactivatedSpaceBanner
          className={styles.banner}
          onButtonClick={() => navigate('/')}
          cypress="space-deactivated-banner"
          message={t(
            'listingDetails:deactivatedMessage',
            'This listing has been deactivated.'
          )}
          buttonLabel={t(
            'listingDetails:deactivatedCTA',
            'Find available parking'
          )}
        />
      )}
    </div>
  );
};

export default ListingDetails;
